import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { COMPANY } from "../content";
import { useLanguage } from "../i18n";
import { PageHero, Reveal } from "../components/Shared";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const { lang, t, data } = useLanguage();
  const tc = t.contactPage;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(tc.requiredToast);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(tc.successToast);
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tc.errorToast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        title={tc.title}
        subtitle={tc.subtitle}
        image="https://images.unsplash.com/photo-1443527394413-4b820fd08dde?w=1920&q=80"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
            {/* Coordonnées */}
            <div className="lg:col-span-2">
              <Reveal>
                <span className="text-[#2f6fd8] text-xs font-semibold uppercase tracking-[0.25em]">
                  {tc.coordsLabel}
                </span>
                <h2 className="text-3xl font-semibold text-[#1b2653] mt-3 mb-8">{tc.coordsTitle}</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4" data-testid="contact-address">
                    <div className="w-11 h-11 rounded-full bg-[#eef3fc] flex items-center justify-center shrink-0">
                      <MapPin size={19} className="text-[#2547a8]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1b2653] text-sm mb-1">{tc.address}</div>
                      <p className="text-slate-600 text-sm font-light">{lang === "fr" ? COMPANY.address : COMPANY.addressEn}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-testid="contact-phone">
                    <div className="w-11 h-11 rounded-full bg-[#eef3fc] flex items-center justify-center shrink-0">
                      <Phone size={19} className="text-[#2547a8]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1b2653] text-sm mb-1">{tc.phone}</div>
                      <a href={`tel:${COMPANY.phoneRaw}`} className="text-slate-600 text-sm font-light hover:text-[#2547a8] transition-colors">
                        {COMPANY.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-testid="contact-email">
                    <div className="w-11 h-11 rounded-full bg-[#eef3fc] flex items-center justify-center shrink-0">
                      <Mail size={19} className="text-[#2547a8]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1b2653] text-sm mb-1">{tc.email}</div>
                      <a href={`mailto:${COMPANY.email}`} className="text-slate-600 text-sm font-light hover:text-[#2547a8] transition-colors">
                        {COMPANY.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-[#eef3fc] flex items-center justify-center shrink-0">
                      <Clock size={19} className="text-[#2547a8]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1b2653] text-sm mb-1">{tc.hours}</div>
                      <p className="text-slate-600 text-sm font-light">{tc.hoursValue}</p>
                    </div>
                  </div>
                </div>

                {/* Carte */}
                <div className="mt-10 rounded-lg overflow-hidden shadow-md border border-slate-200">
                  <iframe
                    title="INEXPONENS — Conakry"
                    src="https://www.google.com/maps?q=Kaporo,+Ratoma,+Conakry,+Guinea&output=embed"
                    className="w-full h-64 border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </Reveal>
            </div>

            {/* Formulaire */}
            <div className="lg:col-span-3">
              <Reveal delay={100}>
                <form
                  onSubmit={handleSubmit}
                  className="bg-[#f5f7fb] rounded-lg p-8 md:p-10"
                  data-testid="contact-form"
                >
                  <h3 className="text-xl font-semibold text-[#1b2653] mb-7">{tc.formTitle}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-[#1b2653] text-sm">{tc.name}</Label>
                      <Input
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={tc.namePh}
                        className="mt-2 bg-white h-11"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#1b2653] text-sm">{tc.emailField}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder={tc.emailPh}
                        className="mt-2 bg-white h-11"
                        data-testid="contact-input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#1b2653] text-sm">{tc.phoneField}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+224 ..."
                        className="mt-2 bg-white h-11"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1b2653] text-sm">{tc.subject}</Label>
                      <Select
                        value={form.subject}
                        onValueChange={(v) => setForm({ ...form, subject: v })}
                      >
                        <SelectTrigger className="mt-2 bg-white h-11" data-testid="contact-select-subject">
                          <SelectValue placeholder={tc.subjectPh} />
                        </SelectTrigger>
                        <SelectContent>
                          {data.services.map((s) => (
                            <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                          ))}
                          <SelectItem value={tc.other}>{tc.other}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-5">
                    <Label htmlFor="message" className="text-[#1b2653] text-sm">{tc.message}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder={tc.messagePh}
                      rows={6}
                      className="mt-2 bg-white resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-7 inline-flex items-center gap-2.5 rounded-full px-9 py-3.5 bg-[#1b2653] text-white text-[13px] font-semibold uppercase tracking-[0.18em] hover:bg-[#2f6fd8] transition-colors duration-300 disabled:opacity-60"
                    data-testid="contact-submit-btn"
                  >
                    <Send size={15} />
                    {submitting ? tc.sending : tc.send}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
